import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './KeepThanksCard.module.css';

const CONFETTI_COLORS = ['#ff2453', '#20abf3', '#1bc65f', '#f3ca20', '#8b5cf6'];
const CONFETTI_COUNT = 30;

/* Pseudo-aléatoire déterministe par index (même recette que l'OnboardingBridge). */
function rand(i: number, salt: number) {
    return Math.abs(Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453) % 1;
}

function confettiStyle(i: number): CSSProperties {
    const angle = (i / CONFETTI_COUNT) * 2 * Math.PI + rand(i, 1) * 0.5;
    const dist = 80 + rand(i, 2) * 150;
    return {
        '--dx': `${Math.cos(angle) * dist}px`,
        '--dy': `${Math.sin(angle) * dist - 60}px`,
        '--rot': `${(rand(i, 3) - 0.5) * 720}deg`,
        '--d': `${0.35 + rand(i, 4) * 0.15}s`,
        '--clr': CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    } as CSSProperties;
}

function IconHeart() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
                className={styles.heartPath}
                d="M12 20.5s-7.5-4.6-9.4-9.3C1.3 7.9 3.4 4.5 6.9 4.5c2 0 3.6 1.1 5.1 3 1.5-1.9 3.1-3 5.1-3 3.5 0 5.6 3.4 4.3 6.7-1.9 4.7-9.4 9.3-9.4 9.3z"
            />
        </svg>
    );
}

/**
 * Remerciement après « Garder Unboared » : remplace le bloc « Garder » (page Mon
 * compte). Badge cœur qui se dessine, onde, éclat de confettis CSS (pas de
 * canvas), puis le message. Animations coupées si l'utilisateur a demandé moins
 * de mouvement (prefers-reduced-motion). Reste affiché jusqu'à ce qu'on quitte
 * la page : c'est la preuve que c'est fait.
 */
export function KeepThanksCard({ detail }: { detail: string }) {
    const { t } = useTranslation();
    const confetti = useMemo(() => Array.from({ length: CONFETTI_COUNT }, (_, i) => confettiStyle(i)), []);

    return (
        <div className={styles.card} role="status">
            <div className={styles.burst} aria-hidden="true">
                <span className={styles.shockwave} />
                {confetti.map((style, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={i} className={styles.confetti} style={style} />
                ))}
                <span className={styles.badge}>
                    <IconHeart />
                </span>
            </div>
            <p className={styles.title}>{t('account.keep.thanksTitle')}</p>
            {detail && <p className={styles.detail}>{detail}</p>}
            <p className={styles.body}>{t('account.keep.thanksBody')}</p>
            <p className={styles.sign}>{t('account.keep.thanksSign')}</p>
        </div>
    );
}
